import { useState } from 'react';
import { useLang } from '../hooks/useLang';
import { LegalPageLayout } from '../components/LegalPageLayout';

const content = {
  EN: {
    back: "Back to Home",
    title: "Privacy Policy",
    updated: "KEH-LG-02 · Version 1.0 · 27 September 2026 · Approved by CEO",
    sections: [
      {
        heading: "1. Introduction",
        body: `This Privacy Policy (the "Policy") explains how Köber Energy Holding S.R.L. ("Köber", "we") collects, uses, stores and protects the personal data of visitors to our websites, of customers, partners, job applicants and other persons with whom we interact.

Controller: Köber Energy Holding S.R.L.
Registered office: 18 Cluceru Udricani street, 4th floor, Bucharest, Romania
Trade Register no. / CUI: J2025042093001 / 51959799
Data protection e-mail: office@koberholding.com
Websites covered: www.koberholding.com · koberenergy.com · utvromania.com · bessintegrator.com · gridpowerx.com

Where a Group company (a "Subsidiary") processes data for its own contracts or services, that company is the controller; its identification details are indicated in the relevant contract or form. Köber Energy Holding S.R.L. and the Subsidiaries may act as joint controllers for shared systems (for example, websites, group CRM), in which case the essence of the arrangement between them is available on request.

Legal framework: We process data in accordance with Regulation (EU) 2016/679 (the "GDPR"), Law no. 190/2018 implementing the GDPR, Law no. 506/2004 on the processing of personal data and the protection of privacy in the electronic communications sector and the decisions of ANSPDCP (the Romanian National Supervisory Authority for Personal Data Processing).

Scope: The Policy applies to all processing carried out through the Group's websites and in our commercial, recruitment and institutional relationships. The processing of employee data is described in a separate internal policy. Third-party websites to which we link have their own policies.`,
      },
      {
        heading: "2. What Data We Process, for What Purpose and on What Basis",
        body: `WEBSITE VISITORS
Data: IP address, browser and device type, pages accessed, date and time, traffic source (server logs)
Purpose: Operation and security of the websites, prevention of abuse, error diagnosis
Legal basis: Legitimate interest (Art. 6(1)(f) GDPR) · Retention: 12 months

Cookie identifiers and usage data · Purpose: Audience measurement, functionalities, marketing — in accordance with the Cookie Policy
Legal basis: Consent (Art. 6(1)(a)); legitimate interest for strictly necessary cookies · Retention: According to the duration of each cookie (maximum 13 months)

PERSONS WHO CONTACT US OR REQUEST A QUOTATION
Data: Surname, first name, e-mail, telephone, company, position, content of the message, attached documents
Purpose: Responding to enquiries, preparing quotations, negotiating and concluding contracts
Legal basis: Performance of a contract or pre-contractual steps (Art. 6(1)(b)); legitimate interest for general enquiries
Retention: 3 years from the last interaction; for the duration of the contract plus the statutory limitation period, if a contract is concluded

CUSTOMERS, SUPPLIERS AND BUSINESS PARTNERS
Data: Identification and contact details of representatives and contact persons, position, signature, bank details of natural persons, correspondence, data from contractual and invoicing documents
Purpose: Performance of contracts, invoicing, accounting records, the business relationship, integrity checks (KYC, sanctions, anti-corruption), management of disputes
Legal basis: Performance of a contract (point (b)); legal obligation (point (c)) for accounting and tax records; legitimate interest (point (f)) for checks and the defence of rights
Retention: For the duration of the contract, then 10 years for financial and accounting documents (Accounting Law no. 82/1991); 5 years for integrity checks

JOB APPLICANTS
Data: CV, cover letter, contact details, education, experience, references, interview results
Purpose: Recruitment and selection
Legal basis: Pre-contractual steps (point (b)); consent for keeping the CV in the applicant database
Retention: For the duration of the recruitment process; up to 2 years with consent for future positions

SUBSCRIBERS TO COMMERCIAL COMMUNICATIONS
Data: E-mail, name, company, preferences, message interaction data (opens, clicks)
Purpose: Sending newsletters, event invitations, information about products and projects
Legal basis: Consent (point (a)); legitimate interest for existing customers, with the right to object (Law no. 506/2004)
Retention: Until withdrawal of consent or unsubscription; 3 years from the last interaction

VISITORS TO PREMISES AND FACILITIES
Data: Name, identity document, company, time of entry and exit, video images (CCTV) in signposted areas
Purpose: Security of persons, energy facilities and property; occupational safety obligations
Legal basis: Legitimate interest (point (f)); legal obligation (point (c)) for critical infrastructure
Retention: Access registers: 1 year; CCTV recordings: 30 days, except in the case of incidents

PERSONS MAKING INTEGRITY REPORTS
Data: Identity of the reporting person (if not anonymous), content of the report, data of the persons concerned
Purpose: Receiving, investigating and resolving reports of breaches of the law
Legal basis: Legal obligation (Law no. 361/2022 on the protection of whistleblowers); legitimate interest
Retention: 5 years from the closure of the report, except for ongoing proceedings

We do not process special categories of data (health, political opinions, trade union membership, etc.) through the websites. We do not make decisions based solely on automated processing that produce legal effects concerning you.`,
      },
      {
        heading: "3. To Whom We Disclose Data",
        body: `Data is accessible only to persons who need it for the purposes above. We may transmit it to:

• The companies of the Köber Group, for the centralised management of business relationships, IT systems and support functions;
• Service providers acting as processors on the basis of contracts compliant with Art. 28 GDPR: web and cloud hosting, e-mail and newsletter platforms, traffic analytics tools, CRM, IT and security services, archiving, recruitment, courier services;
• Professional advisers: lawyers, auditors, accountants, tax advisers, notaries, bound by confidentiality obligations;
• Banks and financial institutions, for payments;
• Public authorities (ANRE, ANAF, the Trade Register, ANSPDCP, courts, criminal investigation bodies), where required by law;
• Prospective purchasers in the event of a reorganisation, merger or sale of assets, subject to confidentiality.

We do not sell or rent personal data.`,
      },
      {
        heading: "4. Transfers Outside the European Economic Area",
        body: `We prefer providers that store data in the European Union. If a provider (for example, a web analytics or e-mail service) transfers data outside the EEA, we ensure that the transfer is made to a country with an adequacy decision of the European Commission, to a recipient certified under the EU-U.S. Data Privacy Framework or on the basis of the Standard Contractual Clauses approved by the Commission, supplemented with additional measures where necessary.

You may obtain a copy of the safeguards at office@koberholding.com.`,
      },
      {
        heading: "5. How Long We Keep Data",
        body: `We keep data only for as long as necessary for the purpose of the processing, in accordance with the periods set out in Chapter 2, after which we delete or anonymise it. The periods may be extended where the law so requires (for example, 10 years for accounting documents) or where the data is necessary for the establishment, exercise or defence of legal claims.`,
      },
      {
        heading: "6. How We Protect Data",
        body: `We apply technical and organisational measures appropriate to the risk: encryption of traffic (HTTPS), role-based access control, two-factor authentication for critical systems, backups, logging, security updates, staff training and confidentiality clauses. Providers are selected and vetted also from a security perspective.

In the event of a personal data breach that presents a risk to your rights, we notify ANSPDCP within 72 hours and, where the risk is high, we inform you directly.`,
      },
      {
        heading: "7. Your Rights",
        body: `Access (Art. 15): To find out whether we process data about you and to receive a copy.
Rectification (Art. 16): To have inaccurate data corrected or completed.
Erasure (Art. 17): To request the deletion of data where it is no longer necessary, you have withdrawn your consent or the processing is unlawful.
Restriction (Art. 18): To limit the processing while we verify a challenge.
Portability (Art. 20): To receive the data you have provided in a structured format or to have it transmitted to another controller.
Objection (Art. 21): To object to processing based on legitimate interest and, at any time, to direct marketing.
Withdrawal of consent (Art. 7): At any time, without affecting the lawfulness of prior processing.
Complaint: To ANSPDCP (B-dul G-ral Gheorghe Magheru nr. 28-30, Bucharest; www.dataprotection.ro) or to the competent courts.

To exercise your rights, write to office@koberholding.com or by post to our registered office, marked "Data Protection". We respond within no more than one month; the period may be extended by two months for complex requests, with notice to you. The exercise of rights is free of charge, except for manifestly unfounded or excessive requests.`,
      },
      {
        heading: "8. Minors",
        body: `Our websites and services are addressed to professionals and adults. We do not knowingly collect data from persons under 16 years of age; if we learn that we have done so, we delete it.`,
      },
      {
        heading: "9. Cookies",
        body: `The use of cookies and similar technologies is described in the Cookie Policy (KEH-LG-03), available on each website, where you can also configure your preferences.`,
      },
      {
        heading: "10. Updates",
        body: `We may update the Policy when the processing operations, providers or legislation change. The version in force, with the date of the last update, is published on the websites; significant changes are announced prominently on the website or by e-mail, where we have your address.`,
      },
      {
        heading: "11. Contact",
        body: `Controller: Köber Energy Holding S.R.L.
Address: 18 Cluceru Udricani street, 4th floor, Bucharest, Romania
E-mail: office@koberholding.com
Web: www.koberholding.com

Supervisory authority: ANSPDCP · www.dataprotection.ro · anspdcp@dataprotection.ro

This version enters into force on 27 September 2026. Document code: KEH-LG-02 · Version 1.0`,
      },
    ],
  },
  RO: {
    back: "Înapoi la Pagina Principală",
    title: "Politica de Confidențialitate",
    updated: "KEH-LG-02 · Versiunea 1.0 · 27 Septembrie 2026 · Aprobată de CEO",
    sections: [
      {
        heading: "1. Introducere",
        body: `Această Politică de Confidențialitate („Politica") explică modul în care Köber Energy Holding S.R.L. („Köber", „noi") colectează, utilizează, stochează și protejează datele cu caracter personal ale vizitatorilor site-urilor noastre web, ale clienților, partenerilor, candidaților la angajare și altor persoane cu care interacționăm.

Operator: Köber Energy Holding S.R.L.
Sediu social: Str. Cluceru Udricani nr. 18, etaj 4, București, România
Nr. Registrul Comerțului / CUI: J2025042093001 / 51959799
E-mail protecția datelor: office@koberholding.com
Site-uri web acoperite: www.koberholding.com · koberenergy.com · utvromania.com · bessintegrator.com · gridpowerx.com

Cadru legal: Prelucrăm datele în conformitate cu Regulamentul (UE) 2016/679 („RGPD"), Legea nr. 190/2018 de implementare a RGPD, Legea nr. 506/2004 privind prelucrarea datelor cu caracter personal și protecția vieții private în sectorul comunicațiilor electronice și deciziile ANSPDCP.

Domeniu de aplicare: Politica se aplică tuturor prelucrărilor efectuate prin intermediul site-urilor web ale Grupului și în relațiile noastre comerciale, de recrutare și instituționale.`,
      },
      {
        heading: "2. Ce Date Prelucrăm, în Ce Scop și pe Ce Bază",
        body: `VIZITATORI AI SITE-ULUI WEB
Date: Adresă IP, tip browser și dispozitiv, pagini accesate, dată și oră, sursă trafic
Scop: Operarea și securitatea site-urilor web, prevenirea abuzurilor, diagnosticarea erorilor
Temei legal: Interes legitim (Art. 6(1)(f) RGPD) · Retenție: 12 luni

PERSOANE CARE NE CONTACTEAZĂ SAU SOLICITĂ O OFERTĂ
Date: Nume, prenume, e-mail, telefon, companie, funcție, conținut mesaj, documente atașate
Scop: Răspunderea la solicitări, pregătirea ofertelor, negocierea și încheierea contractelor
Temei legal: Executarea unui contract sau măsuri precontractuale (Art. 6(1)(b)); interes legitim pentru solicitări generale
Retenție: 3 ani de la ultima interacțiune; pe durata contractului plus termenul de prescripție, dacă se încheie un contract

CLIENȚI, FURNIZORI ȘI PARTENERI DE AFACERI
Date: Date de identificare și contact ale reprezentanților, funcție, semnătură, date bancare ale persoanelor fizice, corespondență, date din documente contractuale și de facturare
Scop: Executarea contractelor, facturare, evidențe contabile, relația de afaceri, verificări de integritate (KYC, sancțiuni, anticorupție), gestionarea litigiilor
Temei legal: Executarea unui contract (lit. (b)); obligație legală (lit. (c)) pentru evidențe contabile și fiscale; interes legitim (lit. (f)) pentru verificări și apărarea drepturilor
Retenție: Pe durata contractului, apoi 10 ani pentru documente financiar-contabile (Legea contabilității nr. 82/1991); 5 ani pentru verificări de integritate

CANDIDAȚI LA ANGAJARE
Date: CV, scrisoare de intenție, date de contact, educație, experiență, referințe, rezultatele interviului
Scop: Recrutare și selecție
Temei legal: Măsuri precontractuale (lit. (b)); consimțământ pentru păstrarea CV-ului în baza de date
Retenție: Pe durata procesului de recrutare; până la 2 ani cu consimțământ pentru posturi viitoare

Nu prelucrăm categorii speciale de date prin intermediul site-urilor web. Nu luăm decizii bazate exclusiv pe prelucrarea automată care produc efecte juridice cu privire la dvs.`,
      },
      {
        heading: "3. Cui Divulgăm Datele",
        body: `Datele sunt accesibile numai persoanelor care au nevoie de ele pentru scopurile de mai sus. Le putem transmite către:

• Companiile din Grupul Köber, pentru gestionarea centralizată a relațiilor de afaceri, sistemelor IT și funcțiilor de suport;
• Furnizori de servicii care acționează ca operatori asociați pe baza unor contracte conforme cu Art. 28 RGPD: găzduire web și cloud, platforme de e-mail și newsletter, instrumente de analiză a traficului, CRM, servicii IT și de securitate, arhivare, recrutare, servicii de curierat;
• Consilieri profesioniști: avocați, auditori, contabili, consultanți fiscali, notari, legați prin obligații de confidențialitate;
• Bănci și instituții financiare, pentru plăți;
• Autorități publice (ANRE, ANAF, Registrul Comerțului, ANSPDCP, instanțe, organe de cercetare penală), unde legea impune;
• Potențiali cumpărători în cazul unei reorganizări, fuziuni sau vânzări de active, cu respectarea confidențialității.

Nu vindem sau închiriem date cu caracter personal.`,
      },
      {
        heading: "4. Transferuri în Afara Spațiului Economic European",
        body: `Preferăm furnizorii care stochează datele în Uniunea Europeană. Dacă un furnizor transferă date în afara SEE, ne asigurăm că transferul se face către o țară cu o decizie de adecvare a Comisiei Europene, către un destinatar certificat în cadrul Cadrului de confidențialitate a datelor UE-SUA sau pe baza Clauzelor Contractuale Standard aprobate de Comisie, completate cu măsuri suplimentare acolo unde este necesar.

Puteți obține o copie a garanțiilor la office@koberholding.com.`,
      },
      {
        heading: "5. Cât Timp Păstrăm Datele",
        body: `Păstrăm datele numai atât timp cât este necesar pentru scopul prelucrării, în conformitate cu perioadele stabilite în Capitolul 2, după care le ștergem sau anonimizăm. Perioadele pot fi prelungite acolo unde legea o impune (de exemplu, 10 ani pentru documente contabile) sau acolo unde datele sunt necesare pentru stabilirea, exercitarea sau apărarea unor drepturi legale.`,
      },
      {
        heading: "6. Cum Protejăm Datele",
        body: `Aplicăm măsuri tehnice și organizatorice adecvate riscului: criptarea traficului (HTTPS), control al accesului bazat pe roluri, autentificare cu doi factori pentru sistemele critice, copii de rezervă, jurnalizare, actualizări de securitate, instruirea personalului și clauze de confidențialitate.

În cazul unei încălcări a securității datelor cu caracter personal care prezintă un risc pentru drepturile dvs., notificăm ANSPDCP în termen de 72 de ore și, acolo unde riscul este ridicat, vă informăm direct.`,
      },
      {
        heading: "7. Drepturile Dvs.",
        body: `Acces (Art. 15): Să aflați dacă prelucrăm date despre dvs. și să primiți o copie.
Rectificare (Art. 16): Să corectați sau să completați datele inexacte.
Ștergere (Art. 17): Să solicitați ștergerea datelor acolo unde nu mai sunt necesare, v-ați retras consimțământul sau prelucrarea este ilegală.
Restricționare (Art. 18): Să limitați prelucrarea în timp ce verificăm o contestație.
Portabilitate (Art. 20): Să primiți datele furnizate într-un format structurat sau să le transmitem unui alt operator.
Opoziție (Art. 21): Să vă opuneți prelucrării bazate pe interes legitim și, în orice moment, marketingului direct.
Retragerea consimțământului (Art. 7): În orice moment, fără a afecta legalitatea prelucrării anterioare.
Plângere: La ANSPDCP (B-dul G-ral Gheorghe Magheru nr. 28-30, București; www.dataprotection.ro) sau la instanțele competente.

Pentru exercitarea drepturilor dvs., scrieți la office@koberholding.com sau prin poștă la sediul nostru social, cu mențiunea „Protecția Datelor". Răspundem în cel mult o lună; termenul poate fi prelungit cu două luni pentru solicitări complexe, cu notificare prealabilă.`,
      },
      {
        heading: "8. Minori",
        body: `Site-urile și serviciile noastre se adresează profesioniștilor și adulților. Nu colectăm în mod deliberat date de la persoane sub 16 ani; dacă aflăm că am făcut-o, le ștergem.`,
      },
      {
        heading: "9. Cookie-uri",
        body: `Utilizarea cookie-urilor și a tehnologiilor similare este descrisă în Politica de Cookie-uri (KEH-LG-03), disponibilă pe fiecare site web, unde vă puteți configura și preferințele.`,
      },
      {
        heading: "10. Actualizări",
        body: `Putem actualiza Politica atunci când se modifică operațiunile de prelucrare, furnizorii sau legislația. Versiunea în vigoare, cu data ultimei actualizări, este publicată pe site-urile web; modificările semnificative sunt anunțate proeminent pe site sau prin e-mail, acolo unde avem adresa dvs.`,
      },
      {
        heading: "11. Contact",
        body: `Operator: Köber Energy Holding S.R.L.
Adresă: Str. Cluceru Udricani nr. 18, etaj 4, București, România
E-mail: office@koberholding.com
Web: www.koberholding.com

Autoritate de supraveghere: ANSPDCP · www.dataprotection.ro · anspdcp@dataprotection.ro

Această versiune intră în vigoare la 27 Septembrie 2026. Cod document: KEH-LG-02 · Versiunea 1.0`,
      },
    ],
  },
};

export default function PrivacyPolicy() {
  const [lang, setLang] = useLang();
  const handleSetLang = setLang;
  return <LegalPageLayout c={content[lang]} lang={lang} setLang={handleSetLang} testIdPrefix="privacy" />;
}
